/**
 * De plaatjes bij de uitleg, getekend en niet gefotografeerd.
 *
 * Schermafbeeldingen zouden herkenbaarder zijn, maar ze verouderen stil: elk
 * scherm dat we aanpassen maakt de uitleg een beetje onwaar en niemand merkt
 * dat. Deze tekeningen zeggen wat er gebeurt, niet hoe elke pixel eruitziet, en
 * blijven daarmee kloppen.
 *
 * Alles hangt aan de kleurtokens, dus ze lopen mee met het thema. Eén vaste
 * verhouding (240 bij 130) zodat het venster niet verspringt tussen de stappen.
 */
const KADER = { className: 'h-auto w-full', viewBox: '0 0 240 130', role: 'presentation' } as const;

/** Warm grijs voor vlakken, de merkkleur voor wat de aandacht heeft. */
const VLAK = 'var(--vs-gray-100)';
const RAND = 'var(--vs-gray-300)';
const DOF = 'var(--vs-gray-400)';
const ACCENT = 'var(--primary)';
const ZACHT = 'var(--vs-sky)';

function Kaart({
  x,
  y,
  w,
  h,
  vulling = 'var(--card)',
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  vulling?: string;
}) {
  return <rect fill={vulling} height={h} rx="8" stroke={RAND} width={w} x={x} y={y} />;
}

/** 1. Waar dit voor is: twee mensen praten, daar komt een beeld uit. */
export function TekeningWaarvoor() {
  return (
    <svg {...KADER}>
      <Kaart h={54} w={92} x={16} y={22} />
      <rect fill={DOF} height={6} rx="3" width={54} x={30} y={38} />
      <rect fill={DOF} height={6} rx="3" width={40} x={30} y={52} />
      <path d="M40 76 L52 88 L52 76 Z" fill="var(--card)" stroke={RAND} />
      <path d="M124 60 L142 52" stroke={DOF} strokeDasharray="4 4" strokeWidth="2" />
      <rect fill={ZACHT} height={28} rx="3" width={16} x={150} y={62} />
      <rect fill={ACCENT} height={52} rx="3" width={16} x={174} y={38} />
      <rect fill={ZACHT} height={40} rx="3" width={16} x={198} y={50} />
      <line stroke={RAND} strokeWidth="2" x1="142" x2="224" y1="94" y2="94" />
    </svg>
  );
}

/** 2. Je gesprekken: een kaart met het spoor van vier stappen. */
export function TekeningGesprekken() {
  // Gecentreerd in de kaart: van 48 tot 192 in een kaart die van 16 tot 224
  // loopt. Stond eerst van 32 tot 176 en hing daardoor scheef.
  const stappen = [0, 1, 2, 3];
  return (
    <svg {...KADER}>
      <Kaart h={86} w={208} x={16} y={22} />
      <rect fill={DOF} height={7} rx="3.5" width={72} x={32} y={38} />
      <rect fill={ACCENT} height={14} rx="7" width={52} x={156} y={35} />
      {stappen.map((i) => (
        <g key={i}>
          {i > 0 && (
            <line
              stroke={i <= 2 ? ACCENT : RAND}
              strokeWidth="2"
              x1={56 + (i - 1) * 48}
              x2={48 + i * 48}
              y1="76"
              y2="76"
            />
          )}
          <circle
            cx={48 + i * 48}
            cy="76"
            fill={i <= 2 ? ACCENT : 'var(--card)'}
            r="8"
            stroke={RAND}
          />
          <rect fill={DOF} height={5} rx="2.5" width={30} x={33 + i * 48} y="92" />
        </g>
      ))}
    </svg>
  );
}

/** 3. Een gesprek beginnen: naam, datum, deelnemers. */
export function TekeningBeginnen() {
  return (
    <svg {...KADER}>
      <Kaart h={96} w={168} x={16} y={18} />
      <rect fill={VLAK} height={16} rx="4" stroke={RAND} width={136} x={32} y={34} />
      <rect fill={VLAK} height={16} rx="4" stroke={RAND} width={136} x={32} y={58} />
      <circle cx="42" cy="92" fill={ZACHT} r="9" />
      <circle cx="60" cy="92" fill={ZACHT} r="9" />
      <circle cx="78" cy="92" fill="var(--card)" r="9" stroke={RAND} strokeDasharray="3 3" />
      <circle cx="196" cy="34" fill={ACCENT} r="14" />
      <path d="M196 27 V41 M189 34 H203" stroke="var(--primary-foreground)" strokeWidth="2.5" />
    </svg>
  );
}

/** 4. Opnemen vanuit de app: microfoon en de meter die meebeweegt. */
export function TekeningOpnemen() {
  const balken = [14, 26, 40, 30, 18, 34, 22];
  return (
    <svg {...KADER}>
      <circle cx="60" cy="65" fill={ACCENT} r="30" />
      <rect fill="var(--primary-foreground)" height={26} rx="7" width={14} x={53} y={50} />
      <path
        d="M46 68 a14 14 0 0 0 28 0"
        fill="none"
        stroke="var(--primary-foreground)"
        strokeWidth="2.5"
      />
      <line stroke="var(--primary-foreground)" strokeWidth="2.5" x1="60" x2="60" y1="82" y2="88" />
      {balken.map((h, i) => (
        <rect
          fill={i % 2 === 0 ? ZACHT : DOF}
          height={h}
          key={i}
          rx="3"
          width={8}
          x={112 + i * 16}
          y={65 - h / 2}
        />
      ))}
    </svg>
  );
}

/** 5. Of lever een bestand aan: uit je eigen recorder of uit een vergadering. */
export function TekeningBestand() {
  return (
    <svg {...KADER}>
      <rect
        fill="var(--card)"
        height={72}
        rx="8"
        stroke={RAND}
        strokeDasharray="6 5"
        width={92}
        x={20}
        y={30}
      />
      <path d="M52 46 h20 l10 10 v34 h-30 z" fill={VLAK} stroke={RAND} />
      <path d="M72 46 v10 h10" fill="none" stroke={RAND} />
      <path
        d="M58 74 q4 -10 8 0 q4 10 8 0"
        fill="none"
        stroke={ACCENT}
        strokeLinecap="round"
        strokeWidth="2.5"
      />
      <line stroke={DOF} strokeDasharray="4 4" strokeWidth="2" x1="120" x2="140" y1="66" y2="66" />
      <Kaart h={72} w={72} x={148} y={30} vulling={VLAK} />
      <rect fill={ZACHT} height={26} rx="4" width={40} x={164} y={44} />
      <path d="M177 51 l12 6 l-12 6 z" fill="var(--card)" />
      <rect fill={DOF} height={5} rx="2.5" width={40} x={164} y={80} />
    </svg>
  );
}

/** 6. Het transcript controleren: lezen en rechtzetten. */
export function TekeningTranscript() {
  return (
    <svg {...KADER}>
      <Kaart h={96} w={140} x={20} y={18} />
      <rect fill={DOF} height={6} rx="3" width={104} x={36} y={34} />
      <rect fill={DOF} height={6} rx="3" width={92} x={36} y={48} />
      <rect fill={ZACHT} height={6} rx="3" width={60} x={36} y={62} />
      <rect fill={DOF} height={6} rx="3" width={104} x={36} y={76} />
      <rect fill={DOF} height={6} rx="3" width={72} x={36} y={90} />
      <circle cx="190" cy="66" fill={ACCENT} r="22" />
      <path
        d="M180 66 l7 7 l13 -14"
        fill="none"
        stroke="var(--primary-foreground)"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="3"
      />
    </svg>
  );
}

/**
 * 7. Hulpvragen beoordelen: een kop per onderwerp, daaronder de rijen.
 *
 * Hier stonden losse pillen, alsof je uit labels kiest. Zo werkt het scherm
 * niet: je ziet een deelonderwerp met de vraag eronder, en een kruisje om er
 * een weg te halen. De tweede rij staat in de waarschuwkleur; dat is wat je
 * ziet als er een naam in de hulpvraag staat.
 */
export function TekeningHulpvragen() {
  const rij = (y: number, deel: number, vraag: number, alarm = false) => (
    <g key={y}>
      <rect fill={VLAK} height={30} rx="6" stroke={RAND} width={176} x={32} y={y} />
      <rect fill={ACCENT} height={6} rx="3" width={deel} x={44} y={y + 7} />
      <rect
        fill={alarm ? 'var(--destructive)' : DOF}
        height={5}
        rx="2.5"
        width={vraag}
        x={44}
        y={y + 18}
      />
      <path
        d={`M191 ${y + 11} l8 8 M199 ${y + 11} l-8 8`}
        stroke={DOF}
        strokeLinecap="round"
        strokeWidth="1.6"
      />
    </g>
  );

  return (
    <svg {...KADER}>
      <Kaart h={96} w={208} x={16} y={18} />
      <rect fill={DOF} height={7} rx="3.5" width={58} x={32} y={32} />
      {rij(48, 44, 108)}
      {rij(84, 36, 124, true)}
    </svg>
  );
}

/** 8. Wat je verder ziet: de zijbalk met de schermen. */
export function TekeningVerder() {
  const items = [0, 1, 2, 3, 4];
  return (
    <svg {...KADER}>
      <Kaart h={104} w={76} x={16} y={14} vulling={VLAK} />
      {items.map((i) => (
        <g key={i}>
          <rect fill={i === 0 ? ACCENT : DOF} height={8} rx="2" width={8} x={28} y={30 + i * 18} />
          <rect fill={i === 0 ? ACCENT : DOF} height={8} rx="4" width={38} x={42} y={30 + i * 18} />
        </g>
      ))}
      <Kaart h={104} w={124} x={100} y={14} />
      <rect fill={DOF} height={7} rx="3.5" width={60} x={114} y={30} />
      <rect fill={ZACHT} height={30} rx="4" width={44} x={114} y={48} />
      <rect fill={ZACHT} height={30} rx="4" width={44} x={166} y={48} />
      <rect fill={VLAK} height={20} rx="4" stroke={RAND} width={96} x={114} y={88} />
    </svg>
  );
}
